/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 * @author Ilya Babanov
 * @author Dmitry Pavlov
 * @version 1.23.0
 * @since 1.22.0
 */
import {
  EConnectionStatusId,
  EEdmAllowedActions,
} from '@/main/pages/PageAccountInfo/types/pageAccountInfoGeneralTypes';
import type {
  IAccountDeliveryInfoData,
  IAccountInfoDeliveryData,
} from '@pages/PageAccountInfo/types/pageAccountInfoTypes';

import type {
  IEdmOperatorConnectionTranslation,
  IModalStatusStart,
} from '../../internationalization/types/modules/edmOperatorConnection';

export const setEdmTooltipTitle = (
  isCoordinator: boolean,
  isEdmDisable: boolean,
  meta: IAccountDeliveryInfoData,
  formValues: IAccountInfoDeliveryData,
  translation: IEdmOperatorConnectionTranslation,
): string => {
  if (isEdmDisable && isCoordinator && meta.edm) {
    const { usePost: formUsePost, useCourier: formUseCourier } = formValues;
    const { edm } = meta;

    if (formUsePost || formUseCourier) {
      return translation.postAndEdmNotAvailable;
    }

    if (!edm.connections.length) {
      const isAllowConnectionAdd = edm.allowedActions.some(
        (item) => item === EEdmAllowedActions.EDM_CONNECTION_ADD,
      );

      return isAllowConnectionAdd
        ? translation.needToConnect
        : translation.wayNotAvailable;
    }

    if (!!edm.connections.length) {
      const connectedRequest = edm.connections.some(
        (item) =>
          item.statusId === EConnectionStatusId.CONNECTED ||
          item.statusId === EConnectionStatusId.DISCONNECT_IN_PROGRESS,
      );

      const isAllowConnectionView = edm.allowedActions.some(
        (item) => item === EEdmAllowedActions.EDM_CONNECTION_VIEW,
      );

      return !connectedRequest && isAllowConnectionView
        ? translation.waitForConnection
        : translation.wayNotAvailable;
    }
  }
  return '';
};

export const setConnectButtonTooltip = (
  isValid: boolean,
  isChangeMode: boolean,
  isInitialOperator: boolean,
  isServiceUser: boolean,
  translation: IModalStatusStart,
): string => {
  if (isChangeMode && isInitialOperator) {
    return translation.changeOperatorTooltipTitle;
  } else if (isServiceUser && isValid) {
    return '';
  } else {
    return translation.createBtnValidation;
  }
};

export const setConnectButtonTooltipMessage = (
  isValid: boolean,
  isChangeMode: boolean,
  isInitialOperator: boolean,
  isServiceUser: boolean,
  serviceUserTooltipMessage: string,
): string => {
  if (isChangeMode && isInitialOperator) {
    return '';
  } else if (isServiceUser && isValid) {
    return serviceUserTooltipMessage;
  }
  return '';
};

export const setConnectButtonTooltipHoverListener = (
  isServiceUser: boolean,
  isChangeMode: boolean,
  isInitialOperator: boolean,
  isValid: boolean,
): boolean => {
  if (isServiceUser) {
    return false;
  }
  if (isChangeMode && isInitialOperator) {
    return false;
  }
  return isValid;
};

export const setEditButtonTooltipHoverListener = (
  isServiceUser: boolean,
  dirty: boolean,
  isValid: boolean,
): boolean => {
  if (isServiceUser) {
    return true;
  } else if (!dirty) {
    return false;
  }
  return isValid;
};

export const setEditButtonTooltip = (
  dirty: boolean,
  isServiceUser: boolean,
  translation: IModalStatusStart,
): string => {
  if (isServiceUser && dirty) {
    return '';
  } else if (!dirty) {
    return translation.editButtonTooltipTitle;
  }
  return translation.createBtnValidation;
};
